// utils/websocket.ts
// Complete WebSocket service with React hooks for real-time communication (TypeScript)

import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiBaseUrl } from '../config';

const API_URL = getApiBaseUrl();

// Type Definitions
interface User {
  id: number;
  name: string;
  email: string;
  company?: string;
  role?: string;
}

interface Activity {
  id: number;
  type: string;
  content: string;
  location: string;
  company?: string;
  wellName?: string;
  userId?: number;
  user?: string;
  status: 'pending' | 'approved' | 'flagged' | 'rejected';
  createdAt: string;
  submittedAt: string;
  isRealTime?: boolean;
  attachments?: string[];
}

interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  read: boolean;
  createdAt: string;
}

interface WebSocketMessage {
  type: string;
  payload: any;
  channel?: string;
  timestamp: string;
}

interface ConnectionState {
  isConnected: boolean;
  isConnecting: boolean;
  reconnectAttempts: number;
  error: string | null;
}

interface DashboardStats {
  onlineUsers: number;
  pendingReports: number;
  flaggedPosts: number;
  totalUsers?: number;
  activeUsers?: number;
  totalPosts?: number;
  systemHealth: 'good' | 'warning' | 'error';
}

interface LocationCoordinates {
  latitude: number;
  longitude: number;
}

interface ChatMessage {
  id: string;
  content: string;
  userId: number;
  userName: string;
  timestamp: string;
  roomId: string;
}

type EventCallback = (payload?: any, data?: any) => void;
type EventType = string;
type ChannelType = string;

// WebSocket Service Class
class WebSocketService {
  private socket: WebSocket | null = null;
  private reconnectAttempts: number = 0;
  private readonly maxReconnectAttempts: number = 5;
  private readonly reconnectDelay: number = 1000;
  private listeners: Map<string, Set<EventCallback>> = new Map();
  private isConnected: boolean = false;
  private connectionPromise: Promise<void> | null = null;

  // Get WebSocket URL based on environment
  private getWebSocketUrl(): string {
    const url = new URL(API_URL);
    url.protocol = url.protocol.replace('http', 'ws');
    url.pathname = '/ws';
    return url.toString();
  }

  // Connect to WebSocket server
  async connect(): Promise<void> {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        const wsUrl = this.getWebSocketUrl();
        const token = localStorage.getItem('token');

        if (!token) {
          this.connectionPromise = null;
          return reject(new Error('No auth token'));
        }

        const finalUrl = `${wsUrl}?token=${encodeURIComponent(token)}`;

        this.socket = new WebSocket(finalUrl);

        this.socket.onopen = () => {
          console.log('WebSocket connected');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.connectionPromise = null;
          
          // Emit connection open event
          this.emit('connection_open');
          
          resolve();
        };

        this.socket.onmessage = (event: MessageEvent) => {
          try {
            const data: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.socket.onclose = (event: CloseEvent) => {
          console.log('WebSocket disconnected:', event.code, event.reason);
          this.isConnected = false;
          this.connectionPromise = null;
          
          // Emit connection close event
          this.emit('connection_close');
          
          // Attempt to reconnect if not a normal closure
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        };

        this.socket.onerror = (error: Event) => {
          console.error('WebSocket error:', error);
          this.connectionPromise = null;
          this.emit('connection_error', error);
          reject(error);
        };

        // Connection timeout
        setTimeout(() => {
          if (this.socket && this.socket.readyState !== WebSocket.OPEN) {
            this.socket.close();
            this.connectionPromise = null;
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000);

      } catch (error) {
        this.connectionPromise = null;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  // Schedule reconnection attempt
  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    console.log(`Scheduling WebSocket reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);
    
    setTimeout(() => {
      this.connect().catch(error => {
        console.error('WebSocket reconnect failed:', error);
      });
    }, delay);
  }

  // Handle incoming messages
  private handleMessage(data: WebSocketMessage): void {
    const { type, payload, channel } = data;
    
    // Emit to specific event listeners
    if (this.listeners.has(type)) {
      this.listeners.get(type)!.forEach(callback => {
        try {
          callback(payload, data);
        } catch (error) {
          console.error('Error in WebSocket listener:', error);
        }
      });
    }

    // Emit to channel listeners
    if (channel && this.listeners.has(`channel:${channel}`)) {
      this.listeners.get(`channel:${channel}`)!.forEach(callback => {
        try {
          callback(payload, data);
        } catch (error) {
          console.error('Error in WebSocket channel listener:', error);
        }
      });
    }

    // Emit to all listeners
    if (this.listeners.has('*')) {
      this.listeners.get('*')!.forEach(callback => {
        try {
          callback(payload, data);
        } catch (error) {
          console.error('Error in WebSocket wildcard listener:', error);
        }
      });
    }
  }

  // Emit event to listeners
  private emit(eventType: EventType, payload: any = {}): void {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType)!.forEach(callback => {
        try {
          callback(payload);
        } catch (error) {
          console.error('Error in WebSocket event listener:', error);
        }
      });
    }
  }

  // Send message to server
  send(type: string, payload: any = {}, channel?: string): boolean {
    if (!this.isConnected || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, cannot send message');
      return false;
    }

    try {
      const message: WebSocketMessage = {
        type,
        payload,
        channel,
        timestamp: new Date().toISOString()
      };
      
      this.socket.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
      return false;
    }
  }

  // Subscribe to specific event types
  on(eventType: EventType, callback: EventCallback): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);

    // Return unsubscribe function
    return () => this.off(eventType, callback);
  }

  // Unsubscribe from event
  off(eventType: EventType, callback: EventCallback): void {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType)!.delete(callback);
      if (this.listeners.get(eventType)!.size === 0) {
        this.listeners.delete(eventType);
      }
    }
  }

  // Subscribe to channel
  subscribe(channel: ChannelType, callback: EventCallback): () => void {
    const channelKey = `channel:${channel}`;
    if (!this.listeners.has(channelKey)) {
      this.listeners.set(channelKey, new Set());
    }
    this.listeners.get(channelKey)!.add(callback);

    // Send subscription message to server
    this.send('subscribe', { channel });

    // Return unsubscribe function
    return () => this.unsubscribe(channel, callback);
  }

  // Unsubscribe from channel
  unsubscribe(channel: ChannelType, callback: EventCallback): void {
    const channelKey = `channel:${channel}`;
    if (this.listeners.has(channelKey)) {
      this.listeners.get(channelKey)!.delete(callback);
      if (this.listeners.get(channelKey)!.size === 0) {
        this.listeners.delete(channelKey);
        // Send unsubscription message to server
        this.send('unsubscribe', { channel });
      }
    }
  }

  // Join a room (for targeted messages)
  joinRoom(roomId: string): void {
    this.send('join_room', { roomId });
  }

  // Leave a room
  leaveRoom(roomId: string): void {
    this.send('leave_room', { roomId });
  }

  // Disconnect WebSocket
  disconnect(): void {
    if (this.socket) {
      this.isConnected = false;
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }
    this.listeners.clear();
    this.reconnectAttempts = 0;
  }

  // Get connection status
  getConnectionStatus(): { isConnected: boolean; readyState: number; reconnectAttempts: number } {
    return {
      isConnected: this.isConnected,
      readyState: this.socket ? this.socket.readyState : WebSocket.CLOSED,
      reconnectAttempts: this.reconnectAttempts
    };
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

// Auto-connect when user is authenticated
const initializeWebSocket = (): void => {
  const user = localStorage.getItem('user');
  const token = localStorage.getItem('token');
  if (user && token && !websocketService.getConnectionStatus().isConnected) {
    websocketService.connect().catch(error => {
      console.error('Failed to initialize WebSocket:', error);
    });
  }
};

// Listen for authentication changes
window.addEventListener('storage', (e: StorageEvent) => {
  if (e.key === 'user' || e.key === 'token') {
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (user && token) {
      initializeWebSocket();
    } else {
      websocketService.disconnect();
    }
  }
});

// Explicitly start WebSocket when needed
export const startWebSocket = () => initializeWebSocket();

// =====================================================
// REACT HOOKS
// =====================================================

// Hook for managing WebSocket connection
export const useWebSocketConnection = (): ConnectionState & {
  connect: () => Promise<void>;
  disconnect: () => void;
} => {
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    isConnected: false,
    isConnecting: false,
    reconnectAttempts: 0,
    error: null
  });

  useEffect(() => {
    const updateConnectionState = () => {
      const status = websocketService.getConnectionStatus();
      setConnectionState(prev => ({
        ...prev,
        isConnected: status.isConnected,
        reconnectAttempts: status.reconnectAttempts
      }));
    };

    // Listen for connection events
    const unsubscribers = [
      websocketService.on('connection_open', () => {
        setConnectionState(prev => ({
          ...prev,
          isConnected: true,
          isConnecting: false,
          error: null
        }));
      }),
      websocketService.on('connection_close', () => {
        setConnectionState(prev => ({
          ...prev,
          isConnected: false,
          isConnecting: false
        }));
      }),
      websocketService.on('connection_error', (error: any) => {
        setConnectionState(prev => ({
          ...prev,
          isConnected: false,
          isConnecting: false,
          error: error.message || 'Connection error'
        }));
      })
    ];

    // Initialize connection
    const user = localStorage.getItem('user');
    if (user) {
      setConnectionState(prev => ({ ...prev, isConnecting: true }));
      websocketService.connect().catch((error: any) => {
        setConnectionState(prev => ({
          ...prev,
          isConnecting: false,
          error: error.message
        }));
      });
    }

    updateConnectionState();

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, []);

  const connect = useCallback((): Promise<void> => {
    setConnectionState(prev => ({ ...prev, isConnecting: true, error: null }));
    return websocketService.connect().catch((error: any) => {
      setConnectionState(prev => ({
        ...prev,
        isConnecting: false,
        error: error.message
      }));
      throw error;
    });
  }, []);

  const disconnect = useCallback((): void => {
    websocketService.disconnect();
    setConnectionState(prev => ({
      ...prev,
      isConnected: false,
      isConnecting: false
    }));
  }, []);

  return {
    ...connectionState,
    connect,
    disconnect
  };
};

// Hook for listening to specific WebSocket events
export const useWebSocketEvent = (
  eventType: EventType, 
  callback: EventCallback, 
  dependencies: React.DependencyList = []
): void => {
  const callbackRef = useRef(callback);
  
  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupListener = async () => {
      // Ensure WebSocket connection is established before subscribing
      if (!websocketService.getConnectionStatus().isConnected) {
        try {
          await websocketService.connect();
        } catch (error) {
          console.error('Failed to connect to WebSocket:', error);
          return;
        }
      }

      const wrappedCallback = (...args: any[]) => {
        callbackRef.current(...args);
      };

      unsubscribe = websocketService.on(eventType, wrappedCallback);
    };

    setupListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [eventType, ...dependencies]);
};

// Hook for real-time notifications
export const useNotifications = (): {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (notificationId: number) => void;
  markAllAsRead: () => void;
  clearNotification: (notificationId: number) => void;
} => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  // Listen for new notifications
  useWebSocketEvent('notification', (notification: Notification) => {
    setNotifications(prev => [notification, ...prev]);
    setUnreadCount(prev => prev + 1);
  });

  // Listen for notification updates
  useWebSocketEvent('notification_read', (data: { notificationId: number }) => {
    const { notificationId } = data;
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, read: true }
          : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  });

  // Listen for bulk notification updates
  useWebSocketEvent('notifications_read_all', () => {
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, read: true }))
    );
    setUnreadCount(0);
  });

  const markAsRead = useCallback((notificationId: number): void => {
    websocketService.send('mark_notification_read', { notificationId });
  }, []);

  const markAllAsRead = useCallback((): void => {
    websocketService.send('mark_all_notifications_read');
  }, []);

  const clearNotification = useCallback((notificationId: number): void => {
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
    websocketService.send('clear_notification', { notificationId });
  }, []);

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotification
  };
};

// Hook for real-time activity feed
export const useActivityFeed = (): {
  activities: Activity[];
  pendingActivities: Activity[];
  submitActivity: (activityData: Partial<Activity>) => void;
  updateActivity: (activityId: number, updates: Partial<Activity>) => void;
  deleteActivity: (activityId: number) => void;
  setActivities: React.Dispatch<React.SetStateAction<Activity[]>>;
} => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [pendingActivities, setPendingActivities] = useState<Activity[]>([]);

  // Listen for new activities
  useWebSocketEvent('new_activity', (activity: Activity) => {
    setActivities(prev => [activity, ...prev]);
  });

  // Listen for activity updates
  useWebSocketEvent('activity_updated', (updatedActivity: Activity) => {
    setActivities(prev => 
      prev.map(activity => 
        activity.id === updatedActivity.id ? updatedActivity : activity
      )
    );
  });

  // Listen for activity deletions
  useWebSocketEvent('activity_deleted', (data: { activityId: number }) => {
    const { activityId } = data;
    setActivities(prev => prev.filter(activity => activity.id !== activityId));
  });

  // Listen for pending activities (admin only)
  useWebSocketEvent('activity_pending_review', (activity: Activity) => {
    setPendingActivities(prev => [activity, ...prev]);
  });

  // Listen for activity approvals
  useWebSocketEvent('activity_approved', (data: { activityId: number }) => {
    const { activityId } = data;
    setPendingActivities(prev => prev.filter(activity => activity.id !== activityId));
  });

  const submitActivity = useCallback((activityData: Partial<Activity>): void => {
    websocketService.send('submit_activity', activityData);
  }, []);

  const updateActivity = useCallback((activityId: number, updates: Partial<Activity>): void => {
    websocketService.send('update_activity', { activityId, updates });
  }, []);

  const deleteActivity = useCallback((activityId: number): void => {
    websocketService.send('delete_activity', { activityId });
  }, []);

  return {
    activities,
    pendingActivities,
    submitActivity,
    updateActivity,
    deleteActivity,
    setActivities
  };
};

// Hook for real-time user presence
export const useUserPresence = (): {
  onlineUsers: User[];
  userStatuses: Map<number, string>;
  updateStatus: (status: string) => void;
  getUserStatus: (userId: number) => string;
} => {
  const [onlineUsers, setOnlineUsers] = useState<User[]>([]);
  const [userStatuses, setUserStatuses] = useState<Map<number, string>>(new Map());

  // Listen for user presence updates
  useWebSocketEvent('user_online', (data: { user: User }) => {
    const { user } = data;
    setOnlineUsers(prev => {
      if (!prev.find(u => u.id === user.id)) {
        return [...prev, user];
      }
      return prev;
    });
    setUserStatuses(prev => new Map(prev.set(user.id, 'online')));
  });

  useWebSocketEvent('user_offline', (data: { userId: number }) => {
    const { userId } = data;
    setOnlineUsers(prev => prev.filter(user => user.id !== userId));
    setUserStatuses(prev => new Map(prev.set(userId, 'offline')));
  });

  useWebSocketEvent('user_status_update', (data: { userId: number; status: string }) => {
    const { userId, status } = data;
    setUserStatuses(prev => new Map(prev.set(userId, status)));
  });

  const updateStatus = useCallback((status: string): void => {
    websocketService.send('update_status', { status });
  }, []);

  const getUserStatus = useCallback((userId: number): string => {
    return userStatuses.get(userId) || 'offline';
  }, [userStatuses]);

  return {
    onlineUsers,
    userStatuses,
    updateStatus,
    getUserStatus
  };
};

// Hook for admin real-time dashboard
export const useAdminDashboard = (): {
  dashboardStats: DashboardStats;
  requestStatsUpdate: () => void;
} => {
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    onlineUsers: 0,
    pendingReports: 0,
    flaggedPosts: 0,
    systemHealth: 'good'
  });

  // Listen for dashboard updates
  useWebSocketEvent('dashboard_stats_update', (stats: Partial<DashboardStats>) => {
    setDashboardStats(prev => ({ ...prev, ...stats }));
  });

  useWebSocketEvent('system_alert', (alert: any) => {
    // Handle system alerts
    console.warn('System Alert:', alert);
  });

  useWebSocketEvent('user_action_required', (data: any) => {
    // Handle actions that require admin intervention
    console.log('Admin action required:', data);
  });

  const requestStatsUpdate = useCallback((): void => {
    websocketService.send('request_dashboard_stats');
  }, []);

  return {
    dashboardStats,
    requestStatsUpdate
  };
};

// Hook for location-based updates
export const useLocationUpdates = (
  coordinates: LocationCoordinates | null, 
  radius: number = 50000
): {
  nearbyActivities: Activity[];
} => {
  const [nearbyActivities, setNearbyActivities] = useState<Activity[]>([]);

  useEffect(() => {
    if (coordinates) {
      // Subscribe to location-based updates
      websocketService.send('subscribe_location', {
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        radius
      });
    }

    return () => {
      websocketService.send('unsubscribe_location');
    };
  }, [coordinates, radius]);

  useWebSocketEvent('nearby_activity', (activity: Activity) => {
    setNearbyActivities(prev => [activity, ...prev]);
  });

  return {
    nearbyActivities
  };
};

// Hook for real-time chat/messaging
export const useChat = (roomId: string | null): {
  messages: ChatMessage[];
  typingUsers: number[];
  sendMessage: (content: string) => void;
  startTyping: () => void;
  stopTyping: () => void;
} => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [typingUsers, setTypingUsers] = useState<number[]>([]);

  useEffect(() => {
    if (roomId) {
      websocketService.joinRoom(roomId);
      // Subscribe to room-specific events
      websocketService.subscribe(`room:${roomId}`, handleRoomMessage);
    }

    return () => {
      if (roomId) {
        websocketService.leaveRoom(roomId);
        websocketService.unsubscribe(`room:${roomId}`, handleRoomMessage);
      }
    };
  }, [roomId]);

  const handleRoomMessage = useCallback((data: any) => {
    const { type, payload } = data;
    
    switch (type) {
      case 'message':
        setMessages(prev => [...prev, payload]);
        break;
      case 'user_typing':
        setTypingUsers(prev => {
          if (!prev.includes(payload.userId)) {
            return [...prev, payload.userId];
          }
          return prev;
        });
        break;
      case 'user_stopped_typing':
        setTypingUsers(prev => prev.filter(id => id !== payload.userId));
        break;
      default:
        break;
    }
  }, []);

  const sendMessage = useCallback((content: string): void => {
    if (roomId) {
      websocketService.send('send_message', { roomId, content });
    }
  }, [roomId]);

  const startTyping = useCallback((): void => {
    if (roomId) {
      websocketService.send('start_typing', { roomId });
    }
  }, [roomId]);

  const stopTyping = useCallback((): void => {
    if (roomId) {
      websocketService.send('stop_typing', { roomId });
    }
  }, [roomId]);

  return {
    messages,
    typingUsers,
    sendMessage,
    startTyping,
    stopTyping
  };
};

// Export the service instance and convenience functions
export default websocketService;

export const useWebSocket = () => {
  return {
    connect: () => websocketService.connect(),
    disconnect: () => websocketService.disconnect(),
    send: (type: string, payload: any, channel?: string) => websocketService.send(type, payload, channel),
    on: (eventType: EventType, callback: EventCallback) => websocketService.on(eventType, callback),
    off: (eventType: EventType, callback: EventCallback) => websocketService.off(eventType, callback),
    subscribe: (channel: ChannelType, callback: EventCallback) => websocketService.subscribe(channel, callback),
    unsubscribe: (channel: ChannelType, callback: EventCallback) => websocketService.unsubscribe(channel, callback),
    joinRoom: (roomId: string) => websocketService.joinRoom(roomId),
    leaveRoom: (roomId: string) => websocketService.leaveRoom(roomId),
    getStatus: () => websocketService.getConnectionStatus()
  };
};